import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { Select } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { CalculationTypeTag } from "@msuite/ui/tag";
import { useTimelineContext } from "@msuite/ui/timeline";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import type { Enums, PartnerTimelineAssignmentReference } from "@msuite/utils/types";
import { useForm } from "react-hook-form";

/** Props Interface */
interface CalculationTypeSelectorProps {
	item: PartnerTimelineAssignmentReference;
}

export function CalculationTypeSelector({ item }: CalculationTypeSelectorProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { organization } = useAuth();
	const { refetch } = useTimelineContext();

	const isHost = item.hostOrgId === organization?.id;

	/** Form */
	const methods = useForm({
		defaultValues: {
			calculationType: item.reference.calculationType,
		},
	});

	/** Functions */
	async function handleOnChangeCalcuationType(calculationType: Enums<"assignmentCalculationType">) {
		try {
			if (!organization?.id) return;
			const response = await client.organizations[":orgId"].partnerships[
				":partnershipId"
			].reference[":referenceId"]["calculation-type"].$patch({
				param: {
					referenceId: item.reference.id,
					partnershipId: item.partnershipId,
					orgId: organization?.id,
				},
				json: {
					calculationType,
				},
			});
			const data = await responseHandler(response);
			if (!data.success) throw Error;
			await refetch();
			toast.success(t("general.successfully_updated"));
		} catch (error) {
			console.error(error);
			toast.error(t("general.unauthorized"));
		}
	}

	if (!isHost) {
		return <CalculationTypeTag type={item.reference.calculationType} />;
	}

	/** Render */
	return (
		<div className="w-max ml-auto">
			<Select
				isRequired={false}
				isNullable={false}
				label={undefined}
				control={methods.control}
				name="calculationType"
				onChange={handleOnChangeCalcuationType}
				options={
					[
						{
							item: { calculationType: "service" },
							label: "Service",
							value: "service",
						},
						{
							item: { calculationType: "hourly" },
							label: "Hourly",
							value: "hourly",
						},
					] as const
				}
				itemNotFoundLabel={t("general.not_found")}
				className="py-1 w-[150px]"
				renderOption={(option) => (
					<CalculationTypeTag
						type={option.calculationType}
						className="mr-4"
					/>
				)}
			/>
		</div>
	);
}
